import React, { FC, ReactNode } from 'react';
import { Redirect, useRouteMatch } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { Capability, Layouts } from 'components';
import { useCurrentUser, useReleases } from 'hooks';
import { isAdminUser, isGroupModeratorUser } from 'helpers';
import * as paths from 'paths';

import AdminNav from './components/AdminNav';
import * as Containers from './containers';

enum AdminLayout {
  OneColumn,
  TwoColumn,
}

const useStyles = makeStyles(theme => ({
  fullWidth: {
    margin: `0 auto ${theme.spacing(4)}px`,
    [theme.breakpoints.up('lg')]: {
      paddingRight: theme.spacing(3),
    },
  },
  maxWidth: {
    maxWidth: theme.breakpoints.values.md,
    [theme.breakpoints.up('lg')]: {
      paddingRight: 0,
    },
  },
}));

const AdminController: FC = () => {
  const match = useRouteMatch();
  const classes = useStyles();
  const currentUser = useCurrentUser();
  const isAdmin = isAdminUser(currentUser);
  const isGroupModerator = isGroupModeratorUser(currentUser);
  const releases = useReleases();

  const hasUM = releases.includes('UserManagement');
  const hasAssignments = releases.includes('Assignments');

  if (!isAdmin && !isGroupModerator) {
    return <Redirect to={paths.index} />;
  }

  const FullWidth = ({ children }: { children: ReactNode }) => (
    <div className={classes.fullWidth}>{children}</div>
  );
  const MaxWidth = ({ children }: { children: ReactNode }) => (
    <div className={classNames(classes.fullWidth, classes.maxWidth)}>{children}</div>
  );

  function renderAdminRoutes() {
    let main = null;
    let layout = AdminLayout.TwoColumn;

    switch (match.path) {
      case paths.adminAnalytics:
        main = <Redirect to={paths.adminAnalyticsOverview} />;
        break;
      case paths.adminAnalyticsOverview:
        main = (
          <Capability name="Admin.Analytics.Overview.View.Access">
            <FullWidth>
              <Containers.AnalyticsOverview />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminAnalyticsGroups:
        main = (
          <Capability name="Admin.Analytics.Groups.View.Access[*]">
            <FullWidth>
              <Containers.AnalyticsGroups />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminAnalyticsUsers:
        main = (
          <Capability name="Admin.Analytics.Users.View.Access[*]">
            <FullWidth>
              <Containers.AnalyticsUsers />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminAnalyticsPosts:
        main = (
          <Capability name="Admin.Analytics.Posts.View.Access[*]">
            <FullWidth>
              <Containers.AnalyticsPosts />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminAnalyticsShares:
        main = (
          <Capability name="Admin.Analytics.Shares.View.Access[*]">
            <FullWidth>
              <Containers.AnalyticsShares />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminAnalyticsLeaderboards:
        main = (
          <Capability name="Admin.Analytics.Leaderboard.View.Access">
            <FullWidth>
              <Containers.AnalyticsLeaderboards />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminPostManagement:
        main = (
          <Capability name="Admin.PostManagement.View.Access[*]">
            <FullWidth>
              <Containers.PostManagement />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminUserManagement:
        if (hasUM) {
          main = (
            <Capability name="Admin.UserManagement.View.Access">
              <FullWidth>
                <Containers.UserManagement />
              </FullWidth>
            </Capability>
          );
        }
        break;
      case paths.adminPeople:
        if (hasUM) {
          break;
        }
        main = (
          <Capability name="Admin.People.View.Access">
            <FullWidth>
              <Containers.People />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminPeopleInvite:
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.People.Invite.View.Access">
            <MaxWidth>
              <Containers.PeopleInvite />
            </MaxWidth>
          </Capability>
        );
        break;
      case paths.adminGroups:
        main = (
          <Capability name="Admin.Groups.View.Access">
            <FullWidth>
              <Containers.Groups />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminInvitations:
        main = (
          <Capability name="Admin.Invitations.View.Access">
            <FullWidth>
              <Containers.Invitations />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminMessages:
        main = (
          <Capability name="Admin.Messages.View.Access">
            <FullWidth>
              <Containers.Messages />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminLeaderboards:
        main = (
          <Capability name="Admin.Leaderboards.View.Access">
            <FullWidth>
              <Containers.Leaderboards />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminContentEmails:
        main = (
          <Capability name="Admin.ContentEmails.View.Access">
            <FullWidth>
              <Containers.ContentEmailsLoader />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminContentEmailEdit:
      case paths.adminContentEmailCreate:
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.ContentEmails.View.Access">
            <MaxWidth>
              <Containers.ContentEmailsLoader />
            </MaxWidth>
          </Capability>
        );
        break;
      case paths.adminSettings:
      case paths.adminSettingsLocations:
      case paths.adminSettingsDisclosures:
      case paths.adminSettingsPolicies:
      case paths.adminSettingsReferrals:
        main = (
          <Capability name="Admin.Settings.Account.View.Access">
            <MaxWidth>
              <Containers.Account />
            </MaxWidth>
          </Capability>
        );
        break;
      case paths.adminSettingsAccount:
        layout = AdminLayout.OneColumn;
        main = (
          <Capability name="Admin.Settings.Account.View.Access">
            <MaxWidth>
              <Containers.AccountSettings />
            </MaxWidth>
          </Capability>
        );
        break;
      case paths.adminSettingsAssignments:
        if (hasAssignments) {
          main = (
            <Capability name="Admin.Settings.Assignments.View.Access">
              <FullWidth>
                <Containers.Assignments />
              </FullWidth>
            </Capability>
          );
        }
        break;
      case paths.adminSettingsAssignmentsCreate:
        if (hasAssignments) {
          layout = AdminLayout.OneColumn;
          main = (
            <Capability name="Admin.Settings.Assignments.View.Access">
              <MaxWidth>
                <Containers.AssignmentCreate />
              </MaxWidth>
            </Capability>
          );
        }
        break;
      case paths.adminSettingsAssignmentsEdit:
        if (hasAssignments) {
          layout = AdminLayout.OneColumn;
          main = (
            <Capability name="Admin.Settings.Assignments.View.Access">
              <MaxWidth>
                <Containers.AssignmentLoader />
              </MaxWidth>
            </Capability>
          );
        }
        break;
      case paths.adminSettingsCompanyOnboarding:
      case paths.adminSettingsCustomOnboarding:
        if (!hasAssignments) {
          main = (
            <Capability name="Admin.Settings.Onboarding.View.Access">
              <MaxWidth>
                <Containers.Onboarding />
              </MaxWidth>
            </Capability>
          );
        }
        break;
      case paths.adminSettingsUserSegments:
        main = (
          <Capability name="Admin.Settings.Segments.View.Access">
            <MaxWidth>
              <Containers.Segments />
            </MaxWidth>
          </Capability>
        );
        break;
      case paths.adminSettingsShareTracking:
        main = (
          <Capability name="Admin.Settings.ShareUrlTracking.View.Access">
            <MaxWidth>
              <Containers.Parameters />
            </MaxWidth>
          </Capability>
        );
        break;
      case paths.adminSettingsSharing:
      case paths.adminSettingsStickers:
        main = (
          <Capability name="Admin.Settings.Sharing.View.Access">
            <MaxWidth>
              <Containers.Sharing />
            </MaxWidth>
          </Capability>
        );
        break;
      case paths.adminUpgrade:
        main = (
          <Capability name="Service.Billing.Generic.Admin.Role">
            <FullWidth>
              <Containers.Upgrade />
            </FullWidth>
          </Capability>
        );
        break;
      case paths.adminBilling:
        main = (
          <Capability name="Service.Billing.Generic.Admin.Role">
            <FullWidth>
              <Containers.Billing />
            </FullWidth>
          </Capability>
        );
        break;
      default:
        throw new Error('Unhandled Admin Route');
    }

    if (layout === AdminLayout.OneColumn) {
      return <Layouts.App1Column navigation desktopSize={12} main={main} />;
    }

    return <Layouts.App2ColumnAdmin desktopSplit="2/10" sidebar={<AdminNav />} main={main} />;
  }

  function renderGroupModeratorRoutes() {
    let main = null;

    switch (match.path) {
      case paths.adminAnalytics:
        main = <Redirect to={paths.adminAnalyticsGroups} />;
        break;
      case paths.adminAnalyticsGroups:
        main = (
          <FullWidth>
            <Containers.AnalyticsGroups />
          </FullWidth>
        );
        break;
      case paths.adminAnalyticsPosts:
        main = (
          <FullWidth>
            <Containers.AnalyticsPosts />
          </FullWidth>
        );
        break;
      case paths.adminAnalyticsShares:
        main = (
          <FullWidth>
            <Containers.AnalyticsShares />
          </FullWidth>
        );
        break;
      case paths.adminAnalyticsUsers:
        main = (
          <FullWidth>
            <Containers.AnalyticsUsers />
          </FullWidth>
        );
        break;
      case paths.adminPostManagement:
        main = (
          <Capability name="Admin.PostManagement.View.Access[*]">
            <FullWidth>
              <Containers.PostManagement />
            </FullWidth>
          </Capability>
        );
        break;
      default:
        throw new Error('Unhandled Group Moderator Route');
    }

    return <Layouts.App2ColumnAdmin desktopSplit="2/10" sidebar={<AdminNav />} main={main} />;
  }

  return isAdmin ? renderAdminRoutes() : renderGroupModeratorRoutes();
};

export default AdminController;
